import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, isValid, parseISO } from 'date-fns';
import { toast } from 'react-hot-toast';
import { AlertCircle, ArrowLeft, Armchair, Clock, MapPin, Minus, Plus, Ticket } from 'lucide-react';
import Header from '@/components/Header';
import { EmptyState, ExpandableText, Img, Shimmer } from '@/components/ui';
import { getSeatAvailability } from '@/api/apiUtils';
import { useEvent } from '@/hooks/useCatalog';
import { formatINR } from '@/lib/catalog';
import { track } from '@/lib/analytics';

const MAX_PER_ORDER = 10;

/** One day in the horizontal date strip. */
const DayChip = ({ day, active, onClick }) => {
  const date = parseISO(day);
  if (!isValid(date)) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-[68px] w-[62px] shrink-0 flex-col items-center justify-center rounded-xl border text-center transition-all ${
        active
          ? 'border-brand-primary bg-brand-primary text-white shadow-card'
          : 'border-ink-200 bg-white text-ink-700 hover:border-ink-400'
      }`}
    >
      <span className="text-xl font-bold leading-none">{format(date, 'd')}</span>
      <span className={`mt-1 text-[11px] font-medium ${active ? 'text-white/75' : 'text-ink-500'}`}>
        {format(date, 'EEE')}
      </span>
    </button>
  );
};

const TicketSelection = () => {
  const { id } = useParams();

  // Reaching ticket selection is the first real purchase intent on the site.
  useEffect(() => {
    track('add_to_cart', { serviceId: id });
  }, [id]);
  const navigate = useNavigate();
  const { event, loading, error } = useEvent(id);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [isSeated, setIsSeated] = useState(false);

  // Probe for a venue layout — seated events skip the qty picker entirely.
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    getSeatAvailability(id)
      .then((res) => {
        const layout = res?.data?.layout;
        if (!cancelled && layout && layout.isSeated !== false) setIsSeated(true);
      })
      .catch(() => {
        /* no layout configured — treat as a general-admission event */
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Default to the first day, the first slot, and any variant named in the URL.
  useEffect(() => {
    if (!event) return;
    setSelectedDate((d) => d || event.days[0] || null);
    setSelectedTime((t) => t || event.times[0] || null);

    const wanted = new URLSearchParams(window.location.search).get('variant');
    if (wanted && event.variants.some((v) => v.id === wanted && !v.soldOut)) {
      setQuantities((q) => (q[wanted] ? q : { ...q, [wanted]: 1 }));
    }
  }, [event]);

  const { totalItems, totalAmount } = useMemo(() => {
    if (!event) return { totalItems: 0, totalAmount: 0 };
    return event.variants.reduce(
      (acc, variant) => {
        const qty = quantities[variant.id] || 0;
        acc.totalItems += qty;
        acc.totalAmount += qty * variant.price;
        return acc;
      },
      { totalItems: 0, totalAmount: 0 }
    );
  }, [event, quantities]);

  // The fee and tax the buyer will be charged, shown before checkout rather
  // than sprung on them there. Mirrors BookingQuoteService: tax on the
  // (pre-discount, at this stage) subtotal, then the category's flat platform
  // fee. `known` is false when the category didn't hydrate — then we say
  // "shown at checkout" instead of guessing a number.
  const charges = useMemo(() => {
    const category = event?.category;
    const platformFee = Number(category?.platformFee) || 0;
    const taxRate = Number(category?.taxRate) || 0;
    const tax = Math.round(totalAmount * taxRate) / 100;
    return {
      known: !!category,
      platformFee,
      taxRate,
      tax,
      total: totalAmount + tax + platformFee,
    };
  }, [event, totalAmount]);

  const setQty = (variant, next) => {
    const capped = Math.max(0, Math.min(next, variant.available, MAX_PER_ORDER));
    if (next > capped && next > 0) {
      toast(
        capped === variant.available
          ? `No more ${variant.name} tickets available`
          : `Up to ${MAX_PER_ORDER} tickets per booking`
      );
    }
    setQuantities((q) => ({ ...q, [variant.id]: capped }));
  };

  const handleContinue = () => {
    if (!selectedDate) return toast.error('Pick a date for the event');
    if (event.times.length > 0 && !selectedTime) return toast.error('Pick a time slot');

    const eventForState = {
      id: event.id,
      title: event.title,
      venue: event.location.address || event.venueName,
      date: selectedDate,
      time: selectedTime,
      image: event.thumbnail,
      description: event.description,
      categoryId: event.categoryId,
      providerId: event.providerId,
      providerServiceId: event.id,
      // Checkout's starting fee, so its total doesn't render without it while
      // the category request is in flight. Checkout still refetches it.
      platformFee: charges.known ? charges.platformFee : undefined,
    };

    if (isSeated) {
      navigate(`/seat-selection/${id}`, {
        state: { event: eventForState, selectedDate, selectedTime, rawEvent: event.raw },
      });
      return;
    }

    if (totalItems === 0) return toast.error('Add at least one ticket');

    const selectedTickets = event.variants
      .filter((variant) => (quantities[variant.id] || 0) > 0)
      .map((variant) => ({
        id: variant.id,
        name: variant.name,
        description: variant.description,
        price: variant.price,
        originalPrice: variant.mrp,
        available: variant.available,
        status: variant.status,
        maxQuantity: variant.available,
        quantity: quantities[variant.id],
        pricePerTicket: variant.price,
        totalPrice: variant.price * quantities[variant.id],
      }));

    navigate(`/book/${id}`, {
      state: {
        event: eventForState,
        selectedTickets,
        totalAmount,
        totalItems,
        selectedDate,
        selectedTime,
        rawEvent: event.raw,
      },
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-muted">
        <Header />
        <div className="lk-container space-y-6 py-8">
          <Shimmer className="h-24 w-full rounded-2xl" />
          <Shimmer className="h-32 w-full rounded-2xl" />
          <Shimmer className="h-64 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-surface-muted">
        <Header />
        <div className="lk-container py-20">
          <EmptyState
            icon={AlertCircle}
            title="We couldn't load these tickets"
            message="The event may have ended or been taken offline."
            action={
              <Link to="/" className="lk-btn-primary">
                Browse events
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  // Deep links and stale tabs can land here for an event whose sales haven't
  // opened. The backend refuses the quote either way; bounce with an
  // explanation rather than a checkout that dead-ends.
  if (event.comingSoon) {
    return (
      <div className="min-h-screen bg-surface-muted">
        <Header />
        <div className="lk-container py-20">
          <EmptyState
            icon={AlertCircle}
            title="Tickets aren't on sale yet"
            message={`${event.title} is announced, but booking hasn't opened. Check back soon.`}
            action={
              <Link to={`/event/${event.id}`} className="lk-btn-primary">
                View event
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const canContinue = isSeated || totalItems > 0;

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />

      <main className="flex-1 pb-8">
        <div className="lk-container py-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="lk-focus mb-3 inline-flex min-h-[44px] items-center gap-2 rounded-lg text-sm font-medium text-ink-500 transition-colors hover:text-ink-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to event
          </button>

          {/* Event strip */}
          <div className="lk-card mb-6 flex items-center gap-4 p-4">
            <Img src={event.thumbnail} alt={event.title} className="h-20 w-16 shrink-0 rounded-xl" />
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold text-ink-900 sm:text-xl">{event.title}</h1>
              <p className="mt-1 flex items-center gap-1.5 truncate text-sm text-ink-500">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                {event.venueName}
              </p>
              {selectedDate && isValid(parseISO(selectedDate)) && (
                <p className="mt-0.5 truncate text-sm font-medium text-brand-secondary">
                  {format(parseISO(selectedDate), 'EEEE, d MMMM yyyy')}
                  {selectedTime ? ` · ${selectedTime}` : ''}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              {/* Date & time */}
              <section className="lk-card p-5 sm:p-6">
                <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.15em] text-ink-500">
                  Select date &amp; time
                </h2>

                {event.days.length === 0 ? (
                  <p className="text-sm text-ink-500">The organiser hasn't published dates yet.</p>
                ) : (
                  <div className="scrollbar-hide -mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1">
                    <span className="mr-1 flex h-[68px] w-11 shrink-0 items-center justify-center rounded-xl bg-surface-sunken text-[11px] font-bold uppercase tracking-wider text-ink-500">
                      <span className="-rotate-90">
                        {format(parseISO(event.days[0]), 'MMM')}
                      </span>
                    </span>
                    {event.days.map((day) => (
                      <DayChip
                        key={day}
                        day={day}
                        active={selectedDate === day}
                        onClick={() => setSelectedDate(day)}
                      />
                    ))}
                  </div>
                )}

                {event.times.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-3">
                    {/* `event_time` is a time_range: one showing that runs from
                        times[0] to times[1], not two separate slots. Mapping
                        over it offered the start and the end as competing
                        choices — "7:00 pm" and "9:00 pm" for a single 7-9 show.
                        One option, valued at the start (it becomes
                        scheduledTime) and labelled with the whole range. */}
                    <button
                      type="button"
                      onClick={() => setSelectedTime(event.times[0])}
                      className={`flex items-center gap-2 rounded-xl border px-6 py-3 text-sm font-semibold transition-all ${
                        selectedTime === event.times[0]
                          ? 'border-brand-primary bg-brand-primary text-white shadow-card'
                          : 'border-ink-200 bg-white text-ink-700 hover:border-ink-400'
                      }`}
                    >
                      <Clock className="h-4 w-4" />
                      {event.timeLabel || event.times[0]}
                    </button>
                  </div>
                )}
              </section>

              {/* Tickets */}
              <section className="lk-card p-5 sm:p-6">
                <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.15em] text-ink-500">
                  {isSeated ? 'Seating' : 'Choose tickets'}
                </h2>

                {isSeated ? (
                  <div className="flex gap-3 rounded-xl bg-brand-tint p-4">
                    <Armchair className="h-5 w-5 shrink-0 text-brand-secondary" />
                    <p className="text-sm text-brand-secondary">
                      This event has reserved seating. Pick your date and time above, then continue
                      to choose your exact seats from the venue layout.
                    </p>
                  </div>
                ) : event.variants.length === 0 ? (
                  <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">
                    No ticket types have been published for this event yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {event.variants.map((variant) => {
                      const qty = quantities[variant.id] || 0;
                      return (
                        <motion.div
                          key={variant.id}
                          layout
                          className={`flex items-center justify-between gap-4 rounded-xl border p-4 transition-colors ${
                            qty > 0 ? 'border-brand-secondary bg-brand-tint/40' : 'border-ink-200'
                          } ${variant.soldOut ? 'opacity-60' : ''}`}
                        >
                          <div className="min-w-0">
                            <p className="font-semibold text-ink-900">{variant.name}</p>
                            <ExpandableText text={variant.description} className="mt-0.5" />
                            <p className="mt-1.5 flex items-center gap-2">
                              <span className="text-base font-bold text-ink-900">
                                ₹{formatINR(variant.price)}
                              </span>
                              {variant.mrp && (
                                <>
                                  <span className="text-sm text-ink-400 line-through">
                                    ₹{formatINR(variant.mrp)}
                                  </span>
                                  <span className="rounded bg-green-50 px-1.5 py-0.5 text-[11px] font-bold text-green-700">
                                    {variant.discountPct}% off
                                  </span>
                                </>
                              )}
                            </p>
                            {!variant.soldOut && variant.available <= 10 && (
                              <p className="mt-1 text-xs font-medium text-amber-600">
                                Filling fast
                              </p>
                            )}
                          </div>

                          <div className="shrink-0">
                            {variant.soldOut ? (
                              <span className="rounded-lg bg-surface-sunken px-4 py-2 text-xs font-bold uppercase text-ink-500">
                                Sold out
                              </span>
                            ) : qty > 0 ? (
                              <div className="flex items-center gap-3 rounded-xl bg-brand-primary px-2 py-1.5 text-white">
                                <button
                                  type="button"
                                  aria-label={`Remove one ${variant.name}`}
                                  onClick={() => setQty(variant, qty - 1)}
                                  className="rounded-full p-1.5 transition hover:bg-white/15"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <span className="min-w-[1.25rem] text-center text-base font-bold">
                                  {qty}
                                </span>
                                <button
                                  type="button"
                                  aria-label={`Add one ${variant.name}`}
                                  onClick={() => setQty(variant, qty + 1)}
                                  className="rounded-full p-1.5 transition hover:bg-white/15 disabled:opacity-40"
                                  disabled={qty >= Math.min(variant.available, MAX_PER_ORDER)}
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setQty(variant, 1)}
                                className="rounded-xl border-2 border-brand-primary px-7 py-2.5 text-sm font-bold uppercase tracking-wide text-brand-primary transition-colors hover:bg-brand-primary hover:text-white"
                              >
                                Add
                              </button>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>

            {/* Summary rail */}
            <aside className="hidden lg:block">
              <div className="lk-card sticky top-24 p-5">
                <h2 className="mb-4 text-lg font-bold text-ink-900">Your order</h2>

                {totalItems === 0 ? (
                  <p className="text-sm text-ink-500">
                    {isSeated
                      ? 'Continue to pick your seats from the venue layout.'
                      : 'No tickets added yet.'}
                  </p>
                ) : (
                  <ul className="space-y-3 border-b border-ink-200/70 pb-4">
                    {event.variants
                      .filter((v) => (quantities[v.id] || 0) > 0)
                      .map((variant) => (
                        <li key={variant.id} className="flex justify-between gap-3 text-sm">
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-ink-900">
                              {variant.name}
                            </span>
                            <span className="text-xs text-ink-500">
                              {quantities[variant.id]} × ₹{formatINR(variant.price)}
                            </span>
                          </span>
                          <span className="font-semibold text-ink-900">
                            ₹{formatINR(variant.price * quantities[variant.id])}
                          </span>
                        </li>
                      ))}
                  </ul>
                )}

                {totalItems > 0 && (
                  <dl className="space-y-2 pt-4 text-sm">
                    <div className="flex items-center justify-between">
                      <dt className="text-ink-500">Subtotal</dt>
                      <dd className="tabular font-medium text-ink-900">₹{formatINR(totalAmount)}</dd>
                    </div>
                    {charges.known && charges.tax > 0 && (
                      <div className="flex items-center justify-between">
                        <dt className="text-ink-500">Taxes ({charges.taxRate}%)</dt>
                        <dd className="tabular font-medium text-ink-900">₹{formatINR(charges.tax)}</dd>
                      </div>
                    )}
                    {charges.known && charges.platformFee > 0 && (
                      <div className="flex items-center justify-between">
                        <dt className="text-ink-500">Platform fee</dt>
                        <dd className="tabular font-medium text-ink-900">₹{formatINR(charges.platformFee)}</dd>
                      </div>
                    )}
                    <div className="flex items-center justify-between border-t border-ink-200/70 pt-3">
                      <dt className="font-semibold text-ink-900">{charges.known ? 'Total' : 'Subtotal'}</dt>
                      <dd className="tabular text-xl font-bold text-ink-900">
                        ₹{formatINR(charges.known ? charges.total : totalAmount)}
                      </dd>
                    </div>
                  </dl>
                )}

                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={!canContinue}
                  className="lk-btn-primary mt-5 w-full"
                >
                  {isSeated ? <Armchair className="h-4 w-4" /> : <Ticket className="h-4 w-4" />}
                  {isSeated ? 'Pick seats' : 'Continue'}
                </button>
                {/* ink-500, not ink-400: the lighter grey is ~2.6:1 on white. */}
                <p className="mt-3 text-center text-xs text-ink-500">
                  {charges.known && !isSeated
                    ? 'Have a coupon? Apply it at checkout.'
                    : 'Taxes and fees are shown at checkout.'}
                </p>
              </div>
            </aside>
          </div>
        </div>
      </main>

      {/* Mobile sticky bar */}
      <div className="sticky bottom-0 z-40 border-t border-ink-200 bg-white p-4 shadow-bar lg:hidden">
        <div className="flex items-center justify-between gap-4">
          <div>
            {isSeated ? (
              <p className="text-sm font-medium text-ink-700">Next: pick your seats</p>
            ) : (
              <>
                <p className="tabular text-xl font-bold leading-tight text-ink-900">
                  ₹{formatINR(charges.known && totalItems > 0 ? charges.total : totalAmount)}
                </p>
                <p className="text-xs text-ink-500">
                  {totalItems} ticket{totalItems === 1 ? '' : 's'}
                  {charges.known && totalItems > 0 && charges.platformFee + charges.tax > 0
                    ? ` · incl. ₹${formatINR(charges.platformFee + charges.tax)} fees`
                    : ''}
                </p>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue}
            className="lk-btn-primary flex-1"
          >
            {isSeated ? 'Pick seats' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketSelection;
